import React, { useRef, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartBox from '../../modules/dashboard/components/ChartBox';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

interface ChartJsLineChartProps {
  data: Array<{ x: string; value: string | number; [key: string]: any }>;
  title: string;
  dataKey?: string;
  height?: number;
  stroke?: string;
  strokeWidth?: number;
  yAxisLabel?: string;
  tooltipLabel?: string;
  showGrid?: boolean;
  customTooltipFormatter?: (value: any, name: string) => [string, string];
  customTickFormatter?: (value: any) => string;
  dotProps?: any;
  activeDotProps?: any;
  innerHeader?: React.ReactNode;
}

const ChartJsLineChart = ({
  data,
  title,
  dataKey = 'value',
  height = 350,
  stroke = '#3498db',
  strokeWidth = 3,
  yAxisLabel,
  tooltipLabel,
  showGrid = true,
  customTooltipFormatter,
  customTickFormatter,
  dotProps = { fill: stroke, strokeWidth: 2, r: 5 },
  innerHeader
}: ChartJsLineChartProps) => {
  const chartRef = useRef<ChartJS<'line', number[], string>>(null);

  // Calculate dynamic left padding based on longest Y-axis label
  const calculateDynamicPadding = () => {
    if (data.length === 0) return 60;

    // Get all values and find the longest formatted string
    const allValues = data.map(item => Math.abs(Number(item[dataKey]) || 0));
    const maxValue = Math.max(...allValues);
    const minValue = Math.min(...allValues);
    
    // Test various values that might appear on Y-axis
    const testValues = [
      maxValue,
      minValue,
      maxValue * 0.8,
      maxValue * 1.2,
      Math.ceil(maxValue / 1000) * 1000,
      Math.ceil(maxValue / 100) * 100,
    ].filter(val => val > 0);

    // Format all test values
    const formattedStrings = testValues.map(val => 
      customTickFormatter ? customTickFormatter(val) : val.toLocaleString('en-US')
    );

    // Find the longest string
    const maxStringLength = Math.max(...formattedStrings.map(str => str.length));

    // Calculate padding: base 60px + 8px per character + extra padding for Y-axis label
    const basePadding = 60;
    const charWidth = 8;
    const yAxisLabelPadding = yAxisLabel ? 40 : 0;
    
    const calculatedPadding = basePadding + (maxStringLength * charWidth) + yAxisLabelPadding;

    return Math.min(calculatedPadding, 200); // Cap at 200px
  };

  const chartData = useMemo(() => ({
    labels: data.map(item => item.x),
    datasets: [
      {
        label: tooltipLabel || 'Value',
        data: data.map(item => Number(item[dataKey]) || 0),
        borderColor: stroke,
        backgroundColor: stroke + '20', // Add transparency for area under line
        borderWidth: strokeWidth,
        pointBackgroundColor: dotProps?.fill || stroke,
        pointBorderColor: dotProps?.fill || stroke,
        pointRadius: dotProps?.r || 5,
        pointHoverRadius: 7,
        tension: 0.1, // Smooth curves
      },
    ],
  }), [data, dataKey, stroke, strokeWidth, tooltipLabel, dotProps]);

  const options: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    layout: {
      padding: {
        // left: calculateDynamicPadding(),
        right: 20,
        top: 20,
        bottom: 20,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        position: 'nearest',
        backgroundColor: '#ffffff',
        titleColor: '#333333',
        bodyColor: '#666666',
        borderColor: '#d0d0d0',
        borderWidth: 1,
        cornerRadius: 8,
        shadowOffsetX: 2,
        shadowOffsetY: 2,
        shadowBlur: 8,
        shadowColor: 'rgba(0, 0, 0, 0.1)',
        displayColors: false,
        padding: 12,
        boxPadding: 6,
        usePointStyle: false,
        caretSize: 6,
        caretPadding: 8,
        titleFont: {
          size: 13,
          weight: 'bold',
        },
        bodyFont: {
          size: 12,
          weight: 'normal',
        },
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            
            if (customTooltipFormatter) {
              const [formattedValue, label] = customTooltipFormatter(value, '');
              return `${label}: ${formattedValue}`;
            }
            
            const formattedValue = customTickFormatter ? customTickFormatter(value) : value.toLocaleString('en-US');
            return `${tooltipLabel || 'Value'}: ${formattedValue}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: showGrid,
          color: '#f0f0f0',
        },
        ticks: {
          color: '#666',
          font: {
            size: 12,
          },
        },
        border: {
          color: '#e0e0e0',
        },
      },
      y: {
        grid: {
          display: showGrid,
          color: '#f0f0f0',
        },
        ticks: {
          color: '#666',
          font: {
            size: 12,
          },
          callback: function(value) {
            return customTickFormatter ? customTickFormatter(Number(value)) : Number(value).toLocaleString('en-US');
          },
        },
        border: {
          color: '#e0e0e0',
        },
        title: {
          display: !!yAxisLabel,
          text: yAxisLabel,
          color: '#666',
          font: {
            size: 12,
          },
        },
      },
    },
  }), [data, showGrid, customTickFormatter, yAxisLabel, customTooltipFormatter, tooltipLabel]);

  return (
    <ChartBox title={title}>
      <div className='f j-e'>
        {innerHeader}
      </div>
      <div style={{ height: `${height}px`, position: 'relative', width: "100%" }}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </ChartBox>
  );
};

export default ChartJsLineChart;